import { AlertCircle, Clock, Info, Video, X } from "lucide-react";
import { Button } from "./Button";

interface VideoProcessingMessageProps {
  dismissProcessingMessage?: () => void;
}

const VideoProcessingMessage = ({
  dismissProcessingMessage,
}: VideoProcessingMessageProps) => {
  return (
    <>
      {dismissProcessingMessage ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full overflow-hidden">
            <Child dismissProcessingMessage={dismissProcessingMessage} />
          </div>
        </div>
      ) : (
        <Child />
      )}
    </>
  );
};

export default VideoProcessingMessage;

const Child = ({ dismissProcessingMessage }: VideoProcessingMessageProps) => {
  return (
    <>
      {dismissProcessingMessage && (
        <div className="bg-amber-100 px-6 py-4 border-b border-amber-200 flex items-center justify-between">
          <div className="flex items-center">
            <Video className="h-6 w-6 text-amber-500 mr-3" />
            <h3 className="text-lg font-semibold text-amber-800">
              Video Processing Required
            </h3>
          </div>
          {dismissProcessingMessage && (
            <button
              onClick={dismissProcessingMessage}
              className="text-amber-500 hover:text-amber-700 focus:outline-none"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      )}
      <div
        className={`px-6 py-4 ${
          !dismissProcessingMessage ? "text-sm pt-0" : "text-base"
        }`}
      >
        <div className="flex items-start mb-4">
          <Clock className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
          <div>
            <p className="text-gray-700 font-medium">Processing Time</p>
            <p className="text-gray-600 mt-1">
              One or more videos need to be processed before they can be used.
              Processing may take up to 30 minutes depending on the video size
              and format.
            </p>
          </div>
        </div>
        <div className="flex items-start mb-4">
          <AlertCircle className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
          <div>
            <p className="text-gray-700 font-medium">Scheduled Posts</p>
            <p className="text-gray-600 mt-1">
              If you schedule posts with these videos, they will be published
              automatically once processing is complete, assuming the scheduled
              time has been reached.
            </p>
          </div>
        </div>
        <div className="flex items-start">
          <Info className="h-5 w-5 text-amber-500 mr-3 mt-0.5" />
          <div>
            <p className="text-gray-700 font-medium">
              Why is processing needed?
            </p>
            <p className="text-gray-600 mt-1">
              Videos may need processing if they are larger than 200MB, use
              non-standard codecs, are not in HD resolution, or have missing
              metadata.
            </p>
          </div>
        </div>
      </div>
      {dismissProcessingMessage && (
        <div className="px-6 py-3 flex justify-end">
          <Button onClick={dismissProcessingMessage} variant="secondary">
            I Understand
          </Button>
        </div>
      )}
    </>
  );
};
